import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import toast from "react-hot-toast";
import { fr } from "yup-locales";
import { setLocale } from "yup";
import { cleanerError } from "@/lib/utils";
import {
  Restaurant,
  RestaurantFormData,
} from "@/redux/services/restaurant/restaurant.type";
import {
  useCreateFranchiseRestaurantMutation,
  useCreateOrUpdateRestaurantMutation,
} from "@/redux/services/restaurant/restaurant.api";
import { useAppSelector } from "@/redux/features/hooks";
import { Roles } from "@/redux/services/user/user.type";
import { buildFranchiseRestaurantPayload } from "@/lib/franchiseRestaurant";
import { restaurantSaveErrorMessage } from "@/lib/franchiseRestaurants";

setLocale(fr);

export const schema = yup.object().shape({
  name: yup.string().required().label("Nom"),
  address: yup.string().label("Adresse"),
  countryId: yup.string().label("Pays"),
  currencyId: yup.string().label("Devise"),
});

// Creating a restaurant (owner) also needs a country and a currency.
const createSchema = schema.shape({
  countryId: yup.string().required().label("Pays"),
  currencyId: yup.string().required().label("Devise"),
});

type CreateRestaurantFormData = RestaurantFormData & {
  countryId?: string;
  currencyId?: string;
};

/**
 * Add/edit restaurant form (Paramètres > Restaurants).
 *
 * Creating is an owner action on their current franchise. The restaurant is
 * created without a manager (SFAPI-214); `onCreated` lets the caller chain to
 * the "Ajouter un manager" step for the new restaurant.
 */
export const useCrudRestaurant = (
  closeModal: () => void,
  item?: Restaurant,
  onCreated?: (restaurant: Restaurant) => void
) => {
  const { currentFranchise, currentUser } = useAppSelector((s) => s.user);
  const isOwner = currentUser?.roles?.includes(Roles.owner);
  const isCreate = !item?.id;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    clearErrors,
  } = useForm<CreateRestaurantFormData>({
    // @ts-ignore
    resolver: yupResolver(isCreate ? createSchema : schema),
  });

  const [update, { isLoading: isUpdating }] =
    useCreateOrUpdateRestaurantMutation();
  const [createFranchiseRestaurant, { isLoading: isCreating }] =
    useCreateFranchiseRestaurantMutation();

  useEffect(() => {
    if (errors) {
      cleanerError(errors, clearErrors);
    }
  }, [errors, clearErrors]);

  // French toast for a failed save (403, duplicate name...), no raw keys.
  const showError = (error: unknown) => {
    toast.error(restaurantSaveErrorMessage(error));
  };

  const onSubmit = async (data: CreateRestaurantFormData) => {
    if (isCreate) {
      if (!isOwner || !currentFranchise?.id) {
        toast.error("Seul un propriétaire peut ajouter un restaurant à sa franchise");
        return;
      }

      const res = await createFranchiseRestaurant({
        franchiseId: currentFranchise.id,
        data: buildFranchiseRestaurantPayload({
          name: data.name,
          address: data.address,
          countryId: data.countryId,
          currencyId: data.currencyId,
        }),
      });

      if ("data" in res && res.data) {
        reset();
        closeModal();
        toast.success("Restaurant ajouté. Ajoutez maintenant son manager.");
        onCreated?.(res.data);
      } else if ("error" in res) {
        showError(res.error);
      }
      return;
    }

    // Country and currency are only asked on creation.
    const res = await update({
      id: item?.id,
      // @ts-ignore
      data: { name: data.name, address: data.address },
    });

    if ("data" in res) {
      closeModal();
      toast.success("Restaurant modifié avec succès");
      reset();
    } else if ("error" in res) {
      showError(res.error);
    }
  };

  return {
    register,
    errors,
    onSubmit: handleSubmit(onSubmit),
    isLoading: isCreating || isUpdating,
    isOwner,
  };
};
